import React, {Fragment, useEffect} from "react";
import {useRouter} from "next/router";
import {IAccountPagesProps} from "../../../types/accountPagesTypes";
import {domainUrl} from "../../../helper";
import {GetServerSideProps, NextPage} from "next";
import {useSelector} from "react-redux";
import {IState} from "../../../types/state";
import AccountLayout from "../../../components/account/AccountLayout";
import Head from "next/head";
import AccountPageNewAddresses from "../../../components/account/AccountPageNewAddresses";
import shopApi from "../../../api/shop";
import MainLayout, {ILocales} from "../../../components/ForCWV/Wrappers/MainLayout";
import {AccountTranslations} from "../../../services/translationKeys";
import {layoutProps} from "../../../services/layoutProps";

const Addresses: NextPage<IAccountPagesProps> = (pageProps) => {
    const {
        dbName,
        selectedLocale,
        translationsData,
        breadcrumb,
        defaultLangCode,
        locales
    } = pageProps
    const customer = useSelector((state: IState) => state.customer);
    const router = useRouter()
    const meta_title = translationsData.AddAnAddress
    const meta_description = `Add address page`
    const meta_keywords = ""
    const meta_url = domainUrl(`${dbName}${router.locale !== "catchAll" && router.locale !== defaultLangCode ? "/" + selectedLocale : ""}${router.asPath}`)

    useEffect(() => {
        if (!customer.token && !customer.authenticated) {
            router.push("/").then(r => r);
        }
    }, []);

    return (
        <MainLayout {...pageProps}>
            <Fragment>
                <Head>
                    <title>{meta_title}</title>
                    <meta name="title" content={meta_title}/>
                    <meta name="description" content={meta_description}/>
                    <meta name="keywords" content={meta_keywords}/>
                    <meta name="generator" content="Zegashop.com"/>
                    <meta name="twitter:description" content={meta_description}/>
                    <meta name="twitter:title" content={meta_title}/>
                    <meta property="og:description" content={meta_description}/>
                    <meta property="og:title" content={meta_title}/>
                    <meta property="og:keywords" content={meta_keywords}/>
                    <meta property="og:url" content={meta_url}/>
                    <meta property="og:locale" content={selectedLocale}/>
                    <link rel="canonical" href={meta_url}/>
                    {locales?.length > 0 ? (
                        <>
                            <link rel="alternate" href={domainUrl(`${dbName}${router.asPath}`)} hrefLang="x-default"/>
                            <link rel="alternate" href={domainUrl(`${dbName}${router.asPath}`)} hrefLang={defaultLangCode}/>
                            {locales.map(intl => (
                                <link rel="alternate" href={domainUrl(`${dbName}/${intl.code}${router.asPath}`)} hrefLang={intl.code}/>
                            ))}
                        </>
                    ) : null}
                </Head>
                <AccountLayout dbName={dbName} breadcrumb={breadcrumb} translationsData={translationsData} selectedLocale={selectedLocale}>
                    <AccountPageNewAddresses translationsData={translationsData}/>
                </AccountLayout>
            </Fragment>
        </MainLayout>
    )
}

export const getServerSideProps: GetServerSideProps = async ({locale, req}) => {
    const typedReq = req as unknown as {
        m: { [key: string]: any }[],
        defaultDB: { db_name: string },
        assets: { logo_image: string },
        ipv4: string
    }
    const wrapperProps = await layoutProps({
        requestHeaders: req.headers,
        requestDbName: typedReq.defaultDB.db_name,
        requestModels: typedReq.m,
        requestLogo: typedReq.assets.logo_image,
        nextJSLocale: locale,
        translationsObject: AccountTranslations,
        ipv4: typedReq.ipv4
    })
    const { maintenanceInfo, suspendStatus, selectedLocale, translationsData: {Home: HomeIntl, Address: AddressIntl, AddAnAddress: AddAnAddressIntl} } = wrapperProps;
    if (String(suspendStatus) === "469") {
        return {
            redirect: { destination: '/suspend', permanent: false },
        }
    }
    if (maintenanceInfo?.is_maintenance_on) {
        return {
            redirect: { destination: '/maintenance', permanent: false },
        }
    }
    const getFields = await shopApi.getFields(selectedLocale, typedReq.m);

    const customFieldGenerate = getFields.map((item: any) => {
        if (item.display_form.split(",").some((item: string) => item === "customer_account")) {
            return item
        }
    }).sort((a: { position: string }, b: { position: string }) => +a.position - +b.position)

    const breadcrumb = [
        {title: HomeIntl, url: `/`},
        {title: AddressIntl, url: `/account/addresses`},
        {title: AddAnAddressIntl, url: ``},
    ]

    return {
        props: {
            breadcrumb,
            locale: selectedLocale,
            customFieldGenerate: customFieldGenerate.filter(Boolean),
            activeMobileNavElement: 4,
            ...wrapperProps,
        },
    };
}

export default Addresses;
